/**
 * BulkActionBar — the contextual bar above a list with ticked rows (ORCH-382).
 *
 * Purpose: One place that says when a selection is a bulk action and how the
 *          bar looks: the selected count, the actions, Clear. Every list that
 *          offers bulk actions renders this through MDTable's `selectionBar`,
 *          so the threshold and the look can't drift per module.
 * Logic: One ticked row is not a bulk action (the row already has its own
 *        actions), so the bar renders nothing below BULK_ACTION_MIN_SELECTED.
 *        Callers decide which actions apply to the selection and disable the
 *        rest with a reason; a disabled action keeps its tooltip.
 * Dependencies: MUI Box, Tooltip, Typography; SecondaryButton / DestructiveButton
 */
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { DestructiveButton, SecondaryButton } from './ActionButtons';

/** Rows that must be ticked before the bar appears. One row has its own actions. */
export const BULK_ACTION_MIN_SELECTED = 2;

export interface BulkAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Tooltip shown instead of nothing while disabled, e.g. "No active users selected". */
  disabledReason?: string;
  testId?: string;
  /** Deactivate, Remove, Delete: drawn as plain red text (ORCH-424 button hierarchy). */
  destructive?: boolean;
}

export interface BulkActionBarProps {
  /** How many rows are selected; below BULK_ACTION_MIN_SELECTED nothing renders. */
  selectedCount: number;
  actions: BulkAction[];
  onClear: () => void;
  /** Locks every action while one runs. */
  working?: boolean;
  /** Rendered after the count, e.g. "Select all N matching your filters". */
  children?: React.ReactNode;
  /** Prefix for the generated test ids: `${prefix}-bar`, `${prefix}-count`, `${prefix}-clear`. */
  testIdPrefix?: string;
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  actions,
  onClear,
  working = false,
  children,
  testIdPrefix = 'bulk',
}) => {
  if (selectedCount < BULK_ACTION_MIN_SELECTED) return null;

  return (
    <Box
      data-testid={`${testIdPrefix}-bar`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        bgcolor: 'action.selected',
      }}
    >
      <Typography variant="body2" fontWeight={700} data-testid={`${testIdPrefix}-count`}>
        {selectedCount} Selected
      </Typography>
      {children}
      {actions.map((action) => {
        const disabled = working || !!action.disabled;
        const ActionButton = action.destructive ? DestructiveButton : SecondaryButton;
        const button = (
          <ActionButton
            size="small"
            disabled={disabled}
            onClick={action.onClick}
            data-testid={action.testId}
          >
            {action.label}
          </ActionButton>
        );
        // A disabled button fires no events, so a span has to carry its tooltip
        return action.disabled && action.disabledReason ? (
          <Tooltip key={action.label} title={action.disabledReason}>
            <span>{button}</span>
          </Tooltip>
        ) : (
          <React.Fragment key={action.label}>{button}</React.Fragment>
        );
      })}
      <SecondaryButton
        size="small"
        onClick={onClear}
        disabled={working}
        data-testid={`${testIdPrefix}-clear`}
      >
        Clear
      </SecondaryButton>
    </Box>
  );
};

export default BulkActionBar;
